import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';

import { styled } from '@compiled/react';
import groupBy from 'lodash/groupBy';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { categoryFilter_softwareReleasesVersionRelatedWork$key } from './__generated__/categoryFilter_softwareReleasesVersionRelatedWork.graphql';
import { Lozenge } from './lozenge/index.tsx';
import messages from './messages.tsx';
import { MoreLozengeAsync as MoreLozenge } from './more-lozenge/async.tsx';

export const RELATED_WORK_CATEGORY_MORE_THRESHOLD = 5;

type Props = {
	relatedWorkEdges: categoryFilter_softwareReleasesVersionRelatedWork$key;
	selectedCategory: string | undefined;
	onCategorySelected: (arg1: string | undefined) => void;
};

export const CategoryFilter = ({
	relatedWorkEdges,
	selectedCategory,
	onCategorySelected,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const tabRefs = useRef<(HTMLElement | null)[]>([]);
	// State to keep track of which tab has keyboard focus
	const [focusedIndex, setFocusedIndex] = useState<number>(-1);
	const edges = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment categoryFilter_softwareReleasesVersionRelatedWork on JiraVersionRelatedWorkV2Edge
			@relay(plural: true) {
				node @required(action: NONE) {
					category @required(action: NONE)
				}
			}
		`,
		relatedWorkEdges,
	);

	const edgesByCategory = groupBy(edges, (edge) => edge?.node.category);

	const sortedCategoriesWithCounts = Object.keys(edgesByCategory)
		.map((category): [string, number] => [category, edgesByCategory[category].length])
		.sort(([categoryA], [categoryB]) => categoryA.localeCompare(categoryB));

	const isknownCategory =
		selectedCategory === undefined ||
		sortedCategoriesWithCounts.some(([category]) => category === selectedCategory);

	// If the selected category is unknown, un-select it (the selected category comes from
	// a query param, so we can't trust it will always be valid).
	useEffect(() => {
		if (!isknownCategory) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			onCategorySelected(undefined);
		}
	}, [isknownCategory, onCategorySelected]);

	const categoriesWithCountWithinThreshold = sortedCategoriesWithCounts.slice(
		0,
		RELATED_WORK_CATEGORY_MORE_THRESHOLD,
	);
	const categoriesWithCountAfterThreshold = sortedCategoriesWithCounts.slice(
		RELATED_WORK_CATEGORY_MORE_THRESHOLD,
	);

	const allCategoryOptions = useMemo(
		() => [
			{ label: formatMessage(messages.all), category: undefined },
			...categoriesWithCountWithinThreshold.map(([category]) => ({ label: category, category })),
		],
		[formatMessage, categoriesWithCountWithinThreshold],
	);

	// Find the index of the selected category
	const selectedIndex = allCategoryOptions.findIndex(
		(option) => option.category === selectedCategory,
	);

	// Handle keyboard navigation. Tabs should be navigable with arrow keys
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			const tabCount = allCategoryOptions.length;
			let newIndex = focusedIndex;

			switch (e.key) {
				case 'ArrowRight':
				case 'ArrowDown':
					e.preventDefault();
					newIndex = (focusedIndex + 1) % tabCount;
					break;
				case 'ArrowLeft':
				case 'ArrowUp':
					e.preventDefault();
					newIndex = (focusedIndex - 1 + tabCount) % tabCount;
					break;
				case 'Home':
					e.preventDefault();
					newIndex = 0;
					break;
				case 'End':
					e.preventDefault();
					newIndex = tabCount - 1;
					break;
				case 'Enter':
				case ' ':
					e.preventDefault();
					if (focusedIndex >= 0 && focusedIndex < tabCount) {
						onCategorySelected(allCategoryOptions[focusedIndex].category);
					}
					return;
				default:
					return;
			}

			// Update focused index and set focus
			setFocusedIndex(newIndex);
			tabRefs.current[newIndex]?.focus();

			// Select the tab when navigating with arrow keys
			if (newIndex >= 0 && newIndex < tabCount) {
				onCategorySelected(allCategoryOptions[newIndex].category);
			}
		},
		[allCategoryOptions, focusedIndex, onCategorySelected],
	);

	// Update refs array when categories change
	useEffect(() => {
		tabRefs.current = tabRefs.current.slice(0, allCategoryOptions.length);
	}, [allCategoryOptions.length]);

	// Set initial focus to the selected tab when the component mounts
	useEffect(() => {
		if (selectedIndex >= 0) {
			setFocusedIndex(selectedIndex);
		}
	}, [selectedIndex]);

	return (
		<Container onKeyDown={handleKeyDown} aria-label={formatMessage(messages.ariaFilterLabel)}>
			<Container role="tablist">
				<Lozenge
					label={formatMessage(messages.all)}
					count={edges.length ?? 0}
					isSelected={selectedCategory === undefined}
					onClick={() => onCategorySelected(undefined)}
					tabIndex={selectedCategory === undefined ? 0 : -1}
					aria-selected={selectedCategory === undefined}
					role="tab"
					ref={(el) => {
						tabRefs.current[0] = el;
					}}
				/>

				{categoriesWithCountWithinThreshold.map(([category, count], index) => (
					<Lozenge
						key={category}
						label={category}
						count={count}
						isSelected={category === selectedCategory}
						onClick={() => onCategorySelected(category)}
						tabIndex={category === selectedCategory ? 0 : -1}
						aria-selected={category === selectedCategory}
						ref={(el) => {
							tabRefs.current[index + 1] = el;
						}}
						role="tab"
					/>
				))}
			</Container>

			{categoriesWithCountAfterThreshold.length > 0 && (
				<MoreLozenge
					categories={categoriesWithCountAfterThreshold.map(
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						([category, count]: [any, any]) => ({
							label: category,
							count,
							isSelected: category === selectedCategory,
							onClick: () => onCategorySelected(category),
							tabIndex: category === selectedCategory ? 0 : -1,
							'aria-selected': category === selectedCategory,
						}),
					)}
				/>
			)}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.100'),
});
